import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { 
  Star, 
  Search, 
  Filter, 
  MessageSquare, 
  Calendar,
  MapPin,
  TrendingUp,
  Users,
  Clock,
  AlertCircle,
  RefreshCw,
  Globe,
  Database,
  Check,
  Eye,
  Send
} from 'lucide-react'
import { useReviews, useReviewStats } from '@/hooks/useReviews'
import { useReviewsFilters } from '@/hooks/useReviewsFilters'
import EnhancedReviewStats from '@/components/reviews/EnhancedReviewStats'
import { SyncStatus } from '@/components/gmb/shared/SyncStatus'
import { SyncStatusIndicator } from '@/components/gmb/shared/SyncStatusWidget'
import { syncReviews } from '@/utils/gmbSync'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { GmbSyncLog } from '@/types/gmb'
import { GmbSyncType } from '@/types/gmb'
import ReviewResponse from '@/components/gmb/reviews/ReviewResponse'

type ReviewSource = 'manual' | 'gmb' | 'jobber' | 'website' | 'other'

export default function ReviewsManagement() {
  const filtersState = useReviewsFilters()
  const { filters, sort, page, pageSize } = filtersState
  const [sourceFilter, setSourceFilter] = useState<ReviewSource | 'all'>('all')
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [isResponseSheetOpen, setIsResponseSheetOpen] = useState(false)
  const [selectedReview, setSelectedReview] = useState<any>(null)
  const queryClient = useQueryClient()
  
  // Temporary placeholder variables - TODO: implement proper response status logic
  const responseStatus = Math.random() > 0.5 ? 'draft' : 'published'
  const hasResponse = Math.random() > 0.5
  
  // Fetch reviews data with source filter
  const reviewsFilters = {
    ...filters,
    ...(sourceFilter !== 'all' && { source: sourceFilter }),
    // Default to GMB source when in GMB integration page
    source: sourceFilter === 'all' ? 'gmb' : sourceFilter
  }
  
  const { 
    data: reviewsData, 
    isLoading: reviewsLoading, 
    error: reviewsError 
  } = useReviews({ filters: reviewsFilters, sort, page, page_size: pageSize })
  
  // Fetch stats data
  const { 
    data: stats, 
    isLoading: statsLoading, 
    error: statsError 
  } = useReviewStats()
  
  // Fetch GMB sync logs
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['gmb-sync-logs', 'reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('sync_type', 'reviews')
        .order('started_at', { ascending: false })
        .limit(10)
      
      if (error) throw error
      return data || []
    }
  })
  
  // Fetch GMB locations
  const { data: gmbLocations = [] } = useQuery({
    queryKey: ['gmb-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('*')
        .eq('is_active', true)
        .order('location_name')
      
      if (error) throw error
      return data || []
    }
  })
  
  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (locationIds?: string[]) => {
      return await syncReviews(locationIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      queryClient.invalidateQueries({ queryKey: ['review-stats'] })
      queryClient.invalidateQueries({ queryKey: ['gmb-sync-logs'] })
    }
  })
  
  // Handle opening response sheet
  const handleOpenResponse = (review: any) => {
    setSelectedReview(review)
    setIsResponseSheetOpen(true)
  }
  
  // Handle closing response sheet
  const handleCloseResponse = () => {
    setSelectedReview(null)
    setIsResponseSheetOpen(false)
  }
  
  // Handle successful response save/publish
  const handleResponseSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['reviews'] })
    queryClient.invalidateQueries({ queryKey: ['review-stats'] })
  }

  if (reviewsError || statsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
            <h3 className="text-lg font-semibold">Error Loading Reviews</h3>
            <p className="text-muted-foreground">
              {reviewsError?.message || statsError?.message || 'Unable to load reviews data'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reviews Management</h1>
          <p className="text-muted-foreground">
            Monitor and respond to reviews from all sources
          </p>
          <div className="mt-2">
            <SyncStatusIndicator syncType={GmbSyncType.REVIEWS} />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.location.href = '/integrations/gmb/sync-reviews'}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            GMB Sync Settings
          </Button>
          <Button variant="outline" size="sm">
            <MessageSquare className="w-4 h-4 mr-2" />
            Response Templates
          </Button>
          <Button size="sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Analytics
          </Button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="enhanced">Enhanced Analytics</TabsTrigger>
          <TabsTrigger value="sync">GMB Sync Status</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          {/* Basic Stats Overview */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats?.total_reviews?.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.reviews_this_month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(stats?.reviews_this_month || 0) >= 0 ? '+' : ''}{stats?.reviews_this_month || 0}
              </span> this month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-1">
              {statsLoading ? '...' : stats?.average_rating || '0'}
              {!statsLoading && stats?.average_rating && (
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      className={`w-4 h-4 ${star <= Math.floor(stats.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.rating_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(stats?.rating_change || 0) >= 0 ? '+' : ''}{stats?.rating_change || 0}
              </span> from last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : `${stats?.response_rate || 0}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.response_rate_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(stats?.response_rate_change || 0) >= 0 ? '+' : ''}{stats?.response_rate_change || 0}%
              </span> this month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Responses</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats?.pending_responses || '0'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.pending_responses && stats.pending_responses > 5 && (
                <span className="text-orange-600">Needs attention</span>
              )}
              {stats?.pending_responses && stats.pending_responses <= 5 && (
                <span className="text-green-600">Under control</span>
              )}
            </p>
          </CardContent>
        </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="enhanced" className="space-y-6">
          <EnhancedReviewStats />
        </TabsContent>
        
        <TabsContent value="sync" className="space-y-6">
          <SyncStatus
            syncLogs={syncLogs}
            isSyncing={syncMutation.isPending}
            onSync={async (syncType) => {
              if (syncType === 'reviews') {
                await syncMutation.mutateAsync(selectedLocations.length > 0 ? selectedLocations : undefined)
              }
              // Handle other sync types if needed
            }}
            showDetailedLogs={true}
          />
        </TabsContent>
      </Tabs>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div>
              <CardTitle>Reviews</CardTitle>
              <CardDescription>
                All reviews from Google My Business and manual entries
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input 
                  placeholder="Search reviews..." 
                  className="pl-10 w-64"
                  value={filters.search || ''}
                  onChange={(e) => filtersState.setSearch(e.target.value)}
                />
              </div>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as ReviewSource | 'all')}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">All Sources</option>
                <option value="gmb">Google My Business</option>
                <option value="manual">Manual Entry</option>
                <option value="jobber">Jobber</option>
                <option value="website">Website</option>
              </select>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Chips */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {filters.search && (
              <Badge variant="secondary">Search: "{filters.search}"</Badge>
            )}
            {filters.location_id && (
              <Badge variant="secondary">Location Filter Active</Badge>
            )}
            {filters.service_id && (
              <Badge variant="secondary">Service Filter Active</Badge>
            )}
            {(filters.rating_min || filters.rating_max) && (
              <Badge variant="secondary">Rating Filter Active</Badge>
            )}
            {(filters.date_from || filters.date_to) && (
              <Badge variant="secondary">Date Filter Active</Badge>
            )}
            {filters.has_response !== undefined && (
              <Badge variant="secondary">
                {filters.has_response ? 'With Responses' : 'No Responses'}
              </Badge>
            )}
            {filtersState.hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={filtersState.clearFilters}
              >
                Clear Filters ({filtersState.filterCount})
              </Button>
            )}
          </div>

          {/* Reviews Table Placeholder */}
          <div className="border rounded-lg">
            {/* Table Header */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border-b bg-muted/50 text-sm font-medium">
              <div className="md:col-span-2">Reviewer</div>
              <div>Location</div>
              <div>Rating</div>
              <div>Date</div>
              <div>Actions</div>
            </div>

            {/* Loading State */}
            {reviewsLoading && (
              <div className="p-8 text-center">
                <div className="text-muted-foreground">Loading reviews...</div>
              </div>
            )}

            {/* No Results */}
            {!reviewsLoading && reviewsData && reviewsData.reviews.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-muted-foreground">
                  {filtersState.hasActiveFilters ? 'No reviews match your filters' : 'No reviews found'}
                </div>
                {filtersState.hasActiveFilters && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={filtersState.clearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {/* Review Rows */}
            {reviewsData?.reviews.map((review) => {
              const reviewSource = review.source || (review.gmb_review_id ? 'gmb' : 'manual')
              const sourceIcon = reviewSource === 'gmb' ? <Globe className="w-3 h-3" /> : <Database className="w-3 h-3" />
              const sourceColor = reviewSource === 'gmb' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              
              return (
              <div key={review.id} className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border-b hover:bg-muted/30 transition-colors">
                <div className="md:col-span-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{review.reviewer_name}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {review.reviewer_comment}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className={`text-xs ${sourceColor}`}>
                          {sourceIcon}
                          <span className="ml-1">{reviewSource.toUpperCase()}</span>
                        </Badge>
                        {review.services && (
                          <Badge variant="outline" className="text-xs">
                            {review.services.name}
                          </Badge>
                        )}
                        {review.gmb_review_id && (
                          <Badge variant="outline" className="text-xs">
                            GMB ID: {review.gmb_review_id.split('/').pop()}
                          </Badge>
                        )}
                        {responseStatus && (
                          <Badge 
                            variant={responseStatus === 'draft' ? 'secondary' : 'default'}
                            className={`text-xs ${responseStatus === 'published' ? 'bg-green-100 text-green-800' : ''}`}
                          >
                            {responseStatus === 'published' && <Check className="w-3 h-3 mr-1" />}
                            {responseStatus.charAt(0).toUpperCase() + responseStatus.slice(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{review.location_name}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= (review.star_rating_number || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      {new Date(review.reviewer_create_time).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleOpenResponse(review)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  {!hasResponse ? (
                    <Button 
                      size="sm"
                      onClick={() => handleOpenResponse(review)}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Respond
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleOpenResponse(review)}
                    >
                      {responseStatus === 'published' ? (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Update
                        </>
                      ) : (
                        <>
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Edit Draft
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              )
            })}
          </div>

          {/* Pagination */}
          {reviewsData && reviewsData.reviews.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, reviewsData.total_count)} of {reviewsData.total_count.toLocaleString()} reviews
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page <= 1}
                  onClick={() => filtersState.setPage(page - 1)}
                >
                  Previous
                </Button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, reviewsData.total_pages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(
                    reviewsData.total_pages - 4,
                    Math.max(1, page - 2)
                  )) + i
                  
                  return pageNum <= reviewsData.total_pages ? (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => filtersState.setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ) : null
                })}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page >= reviewsData.total_pages}
                  onClick={() => filtersState.setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Response Sheet */}
      <Sheet open={isResponseSheetOpen} onOpenChange={setIsResponseSheetOpen}>
        <SheetContent className="w-full sm:w-[600px] sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Review Response</SheetTitle>
            <SheetDescription>
              Manage your response to this customer review
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {selectedReview && (
              <ReviewResponse
                review={selectedReview}
                onClose={handleCloseResponse}
                onSuccess={handleResponseSuccess}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}